/**
 * Sidebar logo row: brand link and collapse toggle
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SidebarBrandProps {
  isCollapsed: boolean;
  isMobileDrawer: boolean;
  isAnonymous: boolean;
  onToggleCollapse: () => void;
  onNavigate: () => void;
}

export const SidebarBrand: React.FC<SidebarBrandProps> = ({
  isCollapsed,
  isMobileDrawer,
  isAnonymous,
  onToggleCollapse,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const collapsed = isCollapsed && !isMobileDrawer;

  return (
    <Box
      className={`flex h-16 shrink-0 items-center border-b border-gray-200 dark:border-slate-700 ${
        collapsed ? 'justify-center px-2' : 'justify-between px-6'
      }`}
    >
      {!collapsed && (
        <Link
          to={isAnonymous ? '/' : '/new-chat'}
          className="flex items-center gap-2 no-underline"
          onClick={onNavigate}
        >
          <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
          <Typography
            variant="h6"
            className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text font-bold text-transparent dark:from-blue-400 dark:to-cyan-400"
          >
            AppAI
          </Typography>
        </Link>
      )}
      {collapsed && (
        <Tooltip title={t('sidebar.expand')} placement="right">
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            aria-label={t('sidebar.expand')}
            className="!p-1 hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
          </IconButton>
        </Tooltip>
      )}
      {/* Hide collapse button in mobile drawer mode */}
      {!isMobileDrawer && !collapsed && (
        <Tooltip
          title={t('sidebar.collapse')}
          placement="right"
        >
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <ChevronLeft size={20} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
